import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  ATLEAST_ONE_CAPITAL_REGEX,
  ATLEAST_ONE_NUMBER_REGEX,
  ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  ATLEAST_ONE_SMALL_REGEX,
  PHONE_NO_LENGTH,
  EMAIL_REGEX,
  TITLE_MAX_LIMIT,
} from "../../../utils/Constants"
import { PHONE_VALIDATION } from "../../../utils/commonValidations";
import moment from "moment";

export const useValidationSchemas = () => {
  const { t } = useTranslation();

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t("restaurant.login.label_email_invalid_format_error"))
      // .test('no-uppercase', t("restaurant.login.label_email_uppercase_error"), function (value) {
      //   if (value) {
      //     return !/[A-Z]/.test(value);
      //   }
      //   return true;
      // })
      .required(t("restaurant.login.label_email_required_error")),
    password: Yup.string().required(t("restaurant.login.validation_password_required"))
  })

  const forgetPasswordValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email("restaurant.forget_password.label_email_invalid_format_error")
      // .test('no-uppercase', "restaurant.forget_password.label_email_uppercase_error", function (value) {
      //   if (value) {
      //     return !/[A-Z]/.test(value);
      //   }
      //   return true;
      // })
      .required("restaurant.forget_password.label_email_required_error"),
  })


  const otpValidationSchema = Yup.object().shape({
    otp: Yup.string()
      .required(t("restaurant.otp_verification.validation_OTP_is_required"))
      .length(4, t("restaurant.otp_verification.otp_error_msg"))
      .matches(/^\d{4}$/, t("restaurant.otp_verification.invalid_otp")),
  });

  const resetPswValidationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(t("restaurant.reset_password.label_new_password_error"))
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("restaurant.reset_password.password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("restaurant.reset_password.Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("restaurant.reset_password.Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("restaurant.reset_password.Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("restaurant.reset_password.Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("Prestaurant.reset_password.assword_Validation_maximum_length")
      ),
    confirmPassword: Yup.string()
      .required(t("restaurant.reset_password.label_confirm_password_error"))
      .oneOf([Yup.ref("newPassword"), null], t("restaurant.reset_password.password_validation_does_not_match")),
  });

  const restaurantProfileValidationSchema = Yup.object({
    restaurantName: Yup.string().required("restaurant.cafe_profile.label_restaurants_error"),
    restaurantAddress: Yup.string().required("restaurant.cafe_profile.restaurant_address_error"),
    // city: Yup.string().required('restaurant.cafe_profile.label_city_error'),
    city: Yup.string()
      .required("restaurant.cafe_profile.label_city_error")
      .matches(/^[A-Za-z\s]+$/, "City can only contain letters"),
    zipCode: Yup.string()
      .matches(/^[0-9]{6}$/, "Invalid Format")
      .required("restaurant.cafe_profile.zip_code_error"),

    website: Yup.string().url("restaurant.cafe_profile.invalid_web_url"),
 
    openingTime: Yup.string().required("restaurant.cafe_profile.opening_time_error"),
    closingTime: Yup.string()
      .required("restaurant.cafe_profile.closing_time_error")
      .test("not_equal_time", "Opening time and closing time cannot be the same.", function (closingTime) {
        const { openingTime } = this.parent;
        if (!openingTime || !closingTime) return true;

        return closingTime !== openingTime;
      })
      .test("greater_time", "restaurant.cafe_profile.rest_validation_closing_time_should_be_greater_than_opening_time", function (closingTime) {
        const { openingTime } = this.parent;
        if (!openingTime || !closingTime) return true;

        return closingTime > openingTime;
      }),
    restaurant_logo: Yup.mixed()
      .nullable()
      .test("fileOrUrl", "admin.cafes.supported_file_error", (value) => {
        if (!value) return true;
        if (typeof value === "string") return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
        return ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type);
      })
      .test("fileSize", "admin.cafes.image_max_size_error", (value) => {
        if (!value || typeof value === "string") return true;
        return value.size <= 1.5 * 1024 * 1024;
      }),
  });

  const profileValidationSchema = Yup.object({
    email: Yup.string().matches(EMAIL_REGEX, 'restaurant.mail_notification_setting.invalid_email').required('restaurant.mail_notification_setting.label_email_error'),
    opening: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    settlement: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    cancel_sales: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    edit_sales: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
  });


  const CashierPasswordValidationSchema = Yup.object({
    cashier_password: Yup.string()
      .required("restaurant.cafe_profile.validation_password_required")
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        "restaurant.cafe_profile.password_validation_lowercase"
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        "restaurant.cafe_profile.Password_Validation_uppercase"
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        "restaurant.cafe_profile.Password_Validation_number"
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        "restaurant.cafe_profile.Password_Validation_special_char"
      )
      .min(PASSWORD_MIN_LENGTH, "restaurant.cafe_profile.Password_Validation_minimum_length")
      .max(
        PASSWORD_MAX_LENGTH,
        "restaurant.cafe_profile.Password_Validation_maximum_length"
      )
  });


  const categoryValidationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required("restaurant.menu.category_name_error"),
    tax_type: Yup.string().required("restaurant.menu.tax_type_error"),
    short_description: Yup.string().trim().required("restaurant.menu.label_short_description_error").max(150, "restaurant.menu.label_short_description_max_length_error"),

    image: Yup.mixed()
      .required("Image is required")
      .test("fileType", "Please upload a valid image format (jpg, jpeg, png, gif, webp).", (value) => {
        if (value && value.type) {
          const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
          console.log('value.type', value.type);
          return validTypes.includes(value.type);
        }
        return true;
      })
      .test("fileSize", "Image size must be less than 1.5 MB.", (value) => {
        if (value && value.size) {
          return value.size <= 1.5 * 1024 * 1024;
        }
        return true;
      }),

  });


  const menuItemsValidationSchema = Yup.object()
    .shape({
      item_name: Yup.string()
        .required('Item name is required')
        .min(3, 'Item name must be 3 characters')
        .max(100, 'Item name must be at most 100 characters'),

      category_id: Yup.string()
        .required('Category is required'),

      item_code: Yup.string()
        .required('Item code is required'),

      short_description: Yup.string()
        .required('Short description is required')
        .trim()
        .max(150, 'restaurant.menu.label_short_description_max_length_error'),

      price: Yup.number()
        .required('Price is required')
        .moreThan(0, 'Price must be greater than 0'),

      cost_price: Yup.number()
        .required('Cost price is required')
        .moreThan(0, 'Cost price must be greater than 0')
        .test(
          'maxDigits',
          'Cost price cannot exceed 10 digits',
          value => !value || value.toString().length <= 10
        )
        .test(
          'less-than-price',
          'Cost price must be less than selling price',
          function (value) {
            const { price } = this.parent;

            // skip comparison if either value is missing
            if (!value || !price) return true;

            return Number(value) < Number(price);
          }
        ),

      // ❌ removed .required() from both
      taxable_price: Yup.number()
        .nullable()
        .max(9999999999, 'Vatable price cannot exceed 9,999,999,999')
        .test(
          'maxDigits',
          'Vatable price cannot exceed 10 digits',
          value => !value || value.toString().length <= 10
        ),

      non_taxable_price: Yup.number()
        .nullable()

        .max(9999999999, 'Non vatable price cannot exceed 9,999,999,999')
        .test(
          'maxDigits',
          'Non vatable price cannot exceed 10 digits',
          value => !value || value.toString().length <= 10
        ),

      image: Yup.mixed()
        .required('Image is required')
        .test(
          'fileType',
          'Supported formats: JPG, JPEG, PNG, WEBP, GIF',
          value =>
            !value ||
            typeof value === 'string' ||
            ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type)
        )
        .test(
          'fileSize',
          'File size should be less than 1.5 MB',
          value =>
            !value ||
            typeof value === 'string' ||
            value.size <= 1.5 * 1024 * 1024
        )
        .test(
          'isValidUrlOrFile',
          'Invalid URL format',
          value =>
            !value ||
            typeof value !== 'string' ||
            /^(ftp|http|https):\/\/[^ "]+$/.test(value)
        ),
    })

    // ✅ Cross-field validation (NO cyclic dependency)
    .test(
      'one-price-required',
      'Either Vatable price or Non-vatable price is required',
      function (values) {
        const { taxable_price, non_taxable_price } = values;
        return Boolean(taxable_price || non_taxable_price);
      }
    );


  const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;
  const staffValidationSchema = Yup.object({
    employee_id: Yup.string().trim().required("restaurant.staff.employee_id_error"),
    first_name:
      Yup.string()
        .trim()
        .required("restaurant.staff.label_first_name_error")
        .max(30, "First name cannot be more than 30 characters")
        .matches(NAME_REGEX, "First name should contain only letters"),
    last_name:
      Yup.string()
        .trim()
        .required("restaurant.staff.label_last_name_error")
        .max(30, "Last name cannot be more than 30 characters")
        .matches(NAME_REGEX, "Last name should contain only letters"),
    // staff_joining_date: Yup.string().trim().required("restaurant.staff.label_joining_date_error"),
    // staff_state: Yup.string(),
    // staff_zip_code: Yup.string(),
    // staff_city: Yup.string(),
    // address: Yup.string().trim(),
    email: Yup.string().trim().required('restaurant.staff.label_email_error').matches(EMAIL_REGEX, 'restaurant.staff.invalid_email'),
    phone_number: Yup.string()
      .test('phone_required', 'restaurant.staff.label_phone_number_required_error', (value) => {
        console.log("value", value)
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'restaurant.staff.label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'restaurant.staff.label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'restaurant.staff.label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),
    password: Yup.string()
      .required(t("restaurant.staff.validation_password_required"))
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("restaurant.staff.password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("restaurant.staff.Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("restaurant.staff.Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("restaurant.staff.Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("restaurant.staff.Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("restaurant.staff.Password_Validation_maximum_length")
      ),
   
    profile_image: Yup.mixed()
      .nullable()
      .test(
        'fileOrUrl',
        'admin.cafes.supported_file_error',
        value => {
          if (!value) return true;
          if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
          return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
        }
      )
      .test(
        'fileSize',
        'admin.cafes.image_max_size_error',
        value => {
          if (!value || typeof value === 'string') return true;
          return value.size <= 1.5 * 1024 * 1024;
        }
      )
      .test(
        'validImage',
        'Upload a valid image',
        value =>
          new Promise(resolve => {
            if (!value || typeof value === 'string') return resolve(true);

            const reader = new FileReader();
            reader.onload = () => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = reader.result;
            };
            reader.onerror = () => resolve(false);
            reader.readAsDataURL(value);
          })
      ),
  });

  const editStaffValidationSchema = Yup.object({

    employee_id: Yup.string()
      .trim()
      .required("restaurant.staff.employee_id_error"),

    first_name: Yup.string()
      .trim()
      .required("restaurant.staff.label_first_name_error")
      .max(30, "First name cannot be more than 30 characters")
      .matches(NAME_REGEX, "First name should contain only letters"),

    last_name: Yup.string()
      .trim()
      .required("restaurant.staff.label_last_name_error")
      .max(30, "Last name cannot be more than 30 characters")
      .matches(NAME_REGEX, "Last name should contain only letters"),


    // staff_joining_date: Yup.string().trim().required("restaurant.staff.label_joining_date_error"),
    // staff_state: Yup.string(),
    // staff_zip_code: Yup.string(),
    // staff_city: Yup.string(),
    // address: Yup.string().trim(),


    email: Yup.string().trim().required('restaurant.staff.label_email_error').matches(EMAIL_REGEX, 'restaurant.staff.invalid_email'),
    phone_number: Yup.string()
      .test('phone_required', 'restaurant.staff.label_phone_number_required_error', (value) => {
        console.log("value", value)
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'restaurant.staff.label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'restaurant.staff.label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'restaurant.staff.label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),
    password: Yup.string()
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("restaurant.staff.password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("restaurant.staff.Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("restaurant.staff.Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("restaurant.staff.Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("restaurant.staff.Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("restaurant.staff.Password_Validation_maximum_length")
      ),
    profile_image: Yup.mixed()
      .nullable()
      .test(
        'fileOrUrl',
        'admin.cafes.supported_file_error',
        value => {
          if (!value) return true;
          if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
          return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
        }
      )
      .test(
        'fileSize',
        'admin.cafes.image_max_size_error',
        value => {
          if (!value || typeof value === 'string') return true;
          return value.size <= 1.5 * 1024 * 1024;
        }
      )
      .test(
        'validImage',
        'Upload a valid image',
        value =>
          new Promise(resolve => {
            if (!value || typeof value === 'string') return resolve(true);

            const reader = new FileReader();
            reader.onload = () => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = reader.result;
            };
            reader.onerror = () => resolve(false);
            reader.readAsDataURL(value);
          })
      ),
  });

  const editDriverValidationSchema = Yup.object({

    name: Yup.string()
      .trim()
      .required("restaurant.staff.label_first_name_error")
      .max(30, "First name cannot be more than 30 characters")
      .matches(NAME_REGEX, "First name should contain only letters"),


    email: Yup.string().trim().required('restaurant.staff.label_email_error').matches(EMAIL_REGEX, 'restaurant.staff.invalid_email'),
    phone_number: Yup.string()
      // REQUIRED
      .required("restaurant.staff.label_phone_number_required_error")

      // ONLY DIGITS (extra safety)
      .matches(/^\d+$/, "restaurant.staff.label_phone_number_digits_error")

      // 🔥 normalize BEFORE validation
      .transform((value) => normalizeMobileNumber(value))

      // MIN LENGTH
      .min(
        PHONE_NO_LENGTH.min,
        "restaurant.staff.label_phone_number_min_length_error"
      )

      // MAX LENGTH
      .max(
        PHONE_NO_LENGTH.max,
        "restaurant.staff.label_phone_number_max_length_error"
      ),
  });

  const normalizeMobileNumber = (input = "") => {
    if (!input) return undefined;

    let digits = input.replace(/\D/g, "");

    if (digits.startsWith("44")) {
      digits = digits.slice(2);
    }

    if (digits.startsWith("0")) {
      digits = digits.slice(1);
    }

    if (digits.length > 10) {
      digits = digits.slice(-10);
    }

    // 🔥 IMPORTANT
    return digits.length ? digits : undefined;
  };


  const driverValidationSchema = Yup.object({

    name: Yup.string()
      .trim()
      .required("Driver name is required")
      .max(30, "Driver name cannot be more than 30 characters")
      .matches(NAME_REGEX, "Driver name should contain only letters"),


    email: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .test(
        'valid-email-if-present',
        'restaurant.staff.invalid_email',
        value => {
          if (!value) return true; // allow empty / null
          return EMAIL_REGEX.test(value);
        }
      ),
    phone_number: Yup.string()
      .required("restaurant.staff.label_phone_number_required_error")
      .trim()
      .test(
        "digits-only",
        "restaurant.staff.label_phone_number_digits_error",
        function (value) {
          if (!value) return true;
          return /^\+?[0-9 ]+$/.test(value);
        }
      )
      .min(
        PHONE_NO_LENGTH.min,
        "restaurant.staff.label_phone_number_min_length_error"
      )
      .max(
        PHONE_NO_LENGTH.max,
        "restaurant.staff.label_phone_number_max_length_error"
      ),

  });

  const tableValidationSchema = Yup.object({
    capacity: Yup.string()
      .trim()
      .required("Table capacity is required")
      .test(
        "capacity_invalid_value",
        "Table capacity must be a valid number",
        (value) => {
          return isNaN(value) === false;
        }
      )
      .test(
        "capacity_min_value",
        "Table capacity cannot be less than 1",
        (value) => {
          const capacity = value ? Number(value) : 0;
          return capacity !== null && capacity >= 1;
        }
      ),
   

    shape: Yup.string().trim().required('restaurant.table_management.table_shape_error')
  });

  const serviceChargeValidationSchema = Yup.object().shape({
    service_charge: Yup.number()
      .typeError("restaurant.service_charges.service_charge_invalid")
      .required("restaurant.service_charges.service_charge_required")
      .integer("restaurant.service_charges.service_charge_integer_error")
      .min(1, "restaurant.service_charges.service_charge_min_error")
      .max(100, "restaurant.service_charges.service_charge_limit_error")
  });




  const addReservationValidationSchema = Yup.object().shape({
    table_id: Yup.string().trim().required("restaurant.table_management.table_error"),
    table_number: Yup.string().trim(),
    number_of_guest: Yup.string()
      .trim()
      .required("restaurant.table_management.number_of_guest_error")
      .test(
        "capacity_min_value",
        "restaurant.table_management.capacity_min_value",
        (value) => {
          const capacity = value ? parseFloat(value) : null;
          return capacity !== null && capacity >= 1;
        }
      )
      .test(
        "capacity_integer",
        "restaurant.table_management.label_capacity_digits_error_guests",
        (value) => {
          const capacity = value ? parseFloat(value) : null;
          return capacity !== null && Number.isInteger(capacity);
        }
      ),
    customer_name:
      Yup.string()
        .trim()
        .max(50, t("restaurant.table_management.err_customer_name_max_length"))
        .matches(/^[A-Za-z\s]+$/, t("restaurant.table_management.err_valid_name"))
        .min(2, t("restaurant.table_management.err_customer_name_min_length"))
        .required("restaurant.table_management.label_customer_name_error"),
    customer_email: Yup.string().trim().required('restaurant.table_management.label_email_error').matches(EMAIL_REGEX, 'restaurant.table_management.invalid_email'),
    customer_phone_number: Yup.string()
      .test('phone_required', 'restaurant.table_management.label_phone_number_required_error', (value) => {
        return value && value.length;
      })
      .test('phone_min_length', 'restaurant.table_management.label_phone_number_min_length_error', (value) => {
        return value && value.length >= PHONE_NO_LENGTH.min;
      })
      .test('phone_max_length', 'restaurant.table_management.label_phone_number_max_length_error', (value) => {
        return value && value.length <= PHONE_NO_LENGTH.max;
      }),

    reservation_date: Yup.string().trim().required("restaurant.table_management.label_date_error"),
    start_time: Yup.string()
      .trim()
      .required("restaurant.table_management.label_start_time_error")
      .test(
        "valid_start_time",
        "restaurant.table_management.label_start_time_passed_error",
        function (value) {
          const { reservation_date } = this.parent;
          if (!reservation_date || !value) return true;

          const now = new Date();
          const selectedDate = new Date(reservation_date);

          let hours, minutes;
          console.log('value--- ', value)
          if (typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value.trim())) {
            // Format: "HH:mm"
            [hours, minutes] = value.split(":").map(Number);
          } else {
            // Format: Date object or full date string
            const parsed = new Date(value);
            hours = parsed.getHours();
            minutes = parsed.getMinutes();
          }

          if (isNaN(hours) || isNaN(minutes)) {
            console.warn("Invalid time parsing:", { value, hours, minutes });
            return true; // skip validation if invalid format
          }
          // Combine date + time into one Date object
          const selectedStart = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            hours,
            minutes,
            0,
            0
          );

          const isToday = selectedDate.toDateString() === now.toDateString();

          console.log("Validation Debug:", {
            isToday,
            selectedStart: selectedStart.toLocaleString(),
            now: now.toLocaleString(),
          });

          // If it's today, ensure the start time is not in the past
          if (isToday && selectedStart.getTime() <= now.getTime()) {
            return this.createError({
              message: "restaurant.table_management.label_start_time_passed_error",
            });
          }
          return true;
        }
      ),

    end_time: Yup.string()
      .required('restaurant.table_management.label_end_time_error')
      .test(
        'greater_time',
        'restaurant.table_management.rest_validation_end_time_should_be_greater_than_start_time',
        function (value) {
          const { start_time, reservation_date } = this.parent;
          console.log("start_time raw:", start_time, "end_time raw:", value);
          if (start_time && value) {
            try {
              const date = new Date(reservation_date);
              const startTime = new Date(start_time);
              const endTime = new Date(value);
              const startHours = startTime.getHours();
              const startMinutes = startTime.getMinutes();
              const endHours = endTime.getHours();
              const endMinutes = endTime.getMinutes();

              // Safety check
              if ([startHours, startMinutes, endHours, endMinutes].some((num) => isNaN(num))) {
                console.warn("Invalid time values", { startHours, startMinutes, endHours, endMinutes, });
                return false; // invalid input → trigger validation error
              }

              const openDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(), startHours, startMinutes, 0,
                0
              );
              const closeDate = new Date(
                date.getFullYear(),
                date.getMonth(), date.getDate(), endHours, endMinutes, 0, 0
              );
              return closeDate.getTime() > openDate.getTime();
            } catch (err) {
              console.log("Time comparison error:", err);
            }
          }
          return true;
        }
      )
      .test(
        'valid_end_time',
        'restaurant.table_management.label_end_time_passed_error',
        function (value) {
          const { reservation_date } = this.parent;
          if (reservation_date && value) {
            const selectedDate = new Date(reservation_date);
            const now = new Date();

            // Check if selected date is today
            const isToday = selectedDate.toDateString() === now.toDateString();
            if (isToday) {
              const [hours, minutes] = value.split(':').map(Number);

              const endTime = new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                selectedDate.getDate(),
                hours,
                minutes,
                0,
                0
              );

              // Must be a future time
              if (endTime.getTime() <= now.getTime()) {
                return this.createError({
                  message: 'restaurant.table_management.label_end_time_passed_error',
                });
              }
            }
          }
          return true;
        }
      ),
  });





  // number_err_msg label_capacity_digits_error
  return {
    loginValidationSchema,
    forgetPasswordValidationSchema,
    resetPswValidationSchema,
    otpValidationSchema,
    restaurantProfileValidationSchema,
    CashierPasswordValidationSchema,
    profileValidationSchema,
    categoryValidationSchema,
    menuItemsValidationSchema,
    staffValidationSchema,
    editStaffValidationSchema,
    tableValidationSchema,
    serviceChargeValidationSchema,
    addReservationValidationSchema,
    editDriverValidationSchema,
    driverValidationSchema
  };
};


